import { useCallback, useEffect, useRef, useState } from "react";
import {
  hasMorePageItems,
  mergeUniquePageItems,
} from "../utils/infinitePagination";

const DEFAULT_ROOT_MARGIN = "360px 0px";

const useInfiniteList = ({
  fetchPage,
  getItemKey,
  pageSize,
  resetKey = "",
  rootMargin = DEFAULT_ROOT_MARGIN,
}) => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [hasMore, setHasMore] = useState(true);

  const sentinelRef = useRef(null);
  const itemsRef = useRef([]);
  const loadingRef = useRef(false);
  const pageRef = useRef(0);

  const loadPage = useCallback(
    async ({ reset = false } = {}) => {
      if (loadingRef.current) return;

      const nextPage = reset ? 0 : pageRef.current;
      loadingRef.current = true;
      setLoading(true);
      setError("");

      try {
        const rows = await fetchPage(nextPage, pageSize);
        const safeRows = Array.isArray(rows) ? rows : [];
        const nextItems = reset
          ? safeRows
          : mergeUniquePageItems(itemsRef.current, safeRows, getItemKey);

        itemsRef.current = nextItems;
        pageRef.current = nextPage + 1;
        setItems(nextItems);
        setHasMore(hasMorePageItems(safeRows.length, pageSize));
      } catch (err) {
        console.error("Error loading infinite list page:", err);
        setError(
          err?.response?.data?.message ||
            err?.message ||
            "Kon de lijst niet laden."
        );
      } finally {
        loadingRef.current = false;
        setLoading(false);
      }
    },
    [fetchPage, getItemKey, pageSize]
  );

  const reset = useCallback(() => loadPage({ reset: true }), [loadPage]);

  useEffect(() => {
    itemsRef.current = [];
    pageRef.current = 0;
    setItems([]);
    setHasMore(true);
    reset();
  }, [reset, resetKey]);

  useEffect(() => {
    const node = sentinelRef.current;
    if (!node || !hasMore || typeof IntersectionObserver === "undefined") {
      return undefined;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries.some((entry) => entry.isIntersecting)) {
          loadPage();
        }
      },
      { rootMargin }
    );

    observer.observe(node);
    return () => observer.disconnect();
  }, [hasMore, loadPage, rootMargin]);

  useEffect(() => {
    const node = sentinelRef.current;
    if (!node || loading || !hasMore || typeof window === "undefined") return;

    const rect = node.getBoundingClientRect();
    if (rect.top <= window.innerHeight + 360) {
      loadPage();
    }
  }, [hasMore, items.length, loadPage, loading]);

  return {
    error,
    hasMore,
    items,
    loading,
    reset,
    sentinelRef,
    setItems,
  };
};

export default useInfiniteList;
